import React, { Component, RefObject } from 'react';
import { TrendingUp, Users, Sparkles, Heart, ArrowLeft } from 'lucide-react';
import apiService from '@/services/api';
import { PostCard } from '@/components/SocialFeed/PostCard';
import { PostFullscreenModal } from '@/components/SocialFeed/PostFullscreenModal';
import { CommentsSection } from '@/components/SocialFeed/CommentsSection';
import styles from './PostFeed.module.scss';

interface Post {
  id: string;
  title?: string;
  description?: string;
  contentText?: string;
  postType: string;
  attachments?: any[];
  author: any;
  likesCount: number;
  dislikesCount: number;
  commentsCount: number;
  sharesCount: number;
  viewsCount: number;
  isLiked: boolean;
  isDisliked: boolean;
  createdAt: string;
  publishedAt?: string;
  status?: 'draft' | 'published';
  tags?: string[];
}

type FeedType = 'top' | 'following' | 'personalized' | 'interests';

interface PostFeedState {
  posts: Post[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  feedType: FeedType;
  selectedPost: Post | null;
  showFullscreen: boolean;
  showComments: boolean;
  commentsPostId: string | null;
  error: string | null;
}

export class PostFeed extends Component<{}, PostFeedState> {
  private observerRef: RefObject<HTMLDivElement> = React.createRef();
  private intersectionObserver: IntersectionObserver | null = null;
  private isLoading: boolean = false; // Prevent duplicate API calls

  constructor(props: {}) {
    super(props);
    this.state = {
      posts: [],
      loading: true,
      loadingMore: false,
      hasMore: true,
      currentPage: 1,
      feedType: 'personalized',
      selectedPost: null,
      showFullscreen: false,
      showComments: false,
      commentsPostId: null,
      error: null,
    };
  }

  componentDidMount() {
    // Only load feed once on mount
    if (!this.isLoading) {
      this.loadFeed();
    }
    this.setupIntersectionObserver();
    this.setupSocketListeners();
  }

  componentWillUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.removeSocketListeners();
  }

  setupIntersectionObserver = () => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && this.state.hasMore && !this.state.loadingMore) {
        this.loadMore();
      }
    }, options);

    if (this.observerRef.current) {
      this.intersectionObserver.observe(this.observerRef.current);
    }
  };

  setupSocketListeners = () => {
    // TODO: Implement socket listeners for real-time updates
    // Listen for:
    // - social:post_published - new posts from followed users
    // - social:post_liked - post like updates
    // - social:post_commented - new comments
    // - social:post_shared - share updates
    // This will replace polling and reduce API calls
  };

  removeSocketListeners = () => {
    // Cleanup socket listeners when component unmounts
  };

  loadFeed = async (page: number = 1, feedType?: FeedType) => {
    // Prevent duplicate API calls
    if (this.isLoading) {
      console.log('[PostFeed] Already loading, skipping duplicate call');
      return;
    }

    const type = feedType || this.state.feedType;
    this.isLoading = true;
    
    this.setState({ 
      loading: page === 1,
      loadingMore: page > 1,
      error: null 
    });

    try {
      const response = await apiService.getAlgorithmFeed({
        page,
        limit: 20,
        feedType: type,
      });

      if (response.success && response.data) {
        const newPosts = response.data.posts || [];
        const pagination = response.data.pagination || {};

        this.setState({
          posts: page === 1 ? newPosts : [...this.state.posts, ...newPosts],
          hasMore: pagination.hasMore !== false,
          currentPage: page,
          loading: false,
          loadingMore: false,
        });

        // Re-observe after loading
        if (this.intersectionObserver && this.observerRef.current) {
          this.intersectionObserver.observe(this.observerRef.current);
        }
      } else {
        throw new Error(response.message || 'Failed to load feed');
      }
    } catch (error: any) {
      console.error('Failed to load feed:', error);
      this.setState({
        error: error.message || 'Failed to load posts. Please try again.',
        loading: false,
        loadingMore: false,
      });
    } finally {
      this.isLoading = false;
    }
  };

  loadMore = () => {
    if (!this.state.hasMore || this.state.loadingMore) return;
    this.loadFeed(this.state.currentPage + 1);
  };

  handleFeedTypeChange = (feedType: FeedType) => {
    this.setState({ feedType, currentPage: 1, hasMore: true });
    this.loadFeed(1, feedType);
  };

  handlePostClick = (post: Post) => {
    this.setState({ selectedPost: post, showFullscreen: true });
  };

  handleCloseFullscreen = () => {
    this.setState({ showFullscreen: false, selectedPost: null });
  };

  handleCommentClick = (postId: string) => {
    this.setState({ showComments: true, commentsPostId: postId });
  };

  handleCloseComments = () => {
    this.setState({ showComments: false, commentsPostId: null });
  };

  handlePostUpdate = (postId: string) => {
    // Update only the specific post instead of reloading entire feed
    // This prevents unnecessary API calls
    // The post state is already updated optimistically in PostCard
    // If we need to refresh, we can fetch just that one post
    // For now, we'll rely on optimistic updates and socket events
  };

  renderFeedTypeSelector = () => {
    const feedTypes: { type: FeedType; label: string; icon: React.ReactNode }[] = [
      { type: 'personalized', label: 'For You', icon: <Sparkles size={18} /> },
      { type: 'following', label: 'Following', icon: <Users size={18} /> },
      { type: 'top', label: 'Top Posts', icon: <TrendingUp size={18} /> },
      { type: 'interests', label: 'Interests', icon: <Heart size={18} /> },
    ];

    return (
      <div className={styles.feedTypeSelector}>
        {feedTypes.map(({ type, label, icon }) => (
          <button
            key={type}
            className={`${styles.feedTypeButton} ${this.state.feedType === type ? styles.active : ''}`}
            onClick={() => this.handleFeedTypeChange(type)}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>
    );
  };

  render() {
    const {
      posts,
      loading,
      loadingMore,
      hasMore,
      error,
      showFullscreen,
      selectedPost,
      showComments,
      commentsPostId,
    } = this.state;

    return (
      <div className={styles.postFeedContainer}>
        <div className={styles.postFeedHeader}>
          <div className={styles.headerTop}>
            <button
              className={styles.goBackButton}
              onClick={() => {
                window.location.href = '/customer/friends';
              }}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>
            <h1 className={styles.feedTitle}>Social Posts</h1>
          </div>
          {this.renderFeedTypeSelector()}
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
            <button onClick={() => this.loadFeed(1)} className={styles.retryButton}>
              Retry
            </button>
          </div>
        )}

        {loading && posts.length === 0 ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className={styles.emptyState}>
            <Sparkles size={48} />
            <h2>No posts yet</h2>
            <p>Start following people or create your first post to see content here!</p>
          </div>
        ) : (
          <div className={styles.postsList}>
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onLike={this.handlePostUpdate}
                onDislike={this.handlePostUpdate}
                onComment={this.handleCommentClick}
                onShare={this.handlePostUpdate}
                onFullscreen={this.handlePostClick}
                onView={undefined}
              />
            ))}

            {/* Infinite scroll trigger */}
            <div ref={this.observerRef} className={styles.scrollTrigger}>
              {loadingMore && (
                <div className={styles.loadingMore}>
                  <div className={styles.spinner}></div>
                  <p>Loading more posts...</p>
                </div>
              )}
              {!hasMore && posts.length > 0 && (
                <div className={styles.endMessage}>
                  <p>You've reached the end of the feed</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Fullscreen Modal */}
        {showFullscreen && selectedPost && (
          <PostFullscreenModal
            post={selectedPost}
            onClose={this.handleCloseFullscreen}
            onLike={this.handlePostUpdate}
            onDislike={this.handlePostUpdate}
            onComment={this.handleCommentClick}
            onShare={this.handlePostUpdate}
          />
        )}

        {/* Comments Section */}
        {showComments && commentsPostId && (
          <CommentsSection
            postId={commentsPostId}
            onClose={this.handleCloseComments}
          />
        )}
      </div>
    );
  }
}

export default PostFeed;

